import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PotentialBadge from './PotentialBadge';
import ScoreBadge from './ScoreBadge';
import ServiceGapsCard from './ServiceGapsCard';
import ITFixesCard from './ITFixesCard';
import api from '../services/api';

interface Business {
  id: number;
  name: string;
  address: string | null;
  rating: number | null;
  has_website: boolean;
  website: string | null;
  review_count?: number;
}

interface BusinessCardProps {
  business: Business;
  onSaveLead?: (businessId: number) => void;
  isSaved?: boolean;
  isSelected?: boolean;
  isInPipeline?: boolean;
  onSelect?: (businessId: number, selected: boolean) => void;
  showCheckbox?: boolean;
}

const BusinessCard: React.FC<BusinessCardProps> = ({ 
  business, 
  onSaveLead, 
  isSaved = false,
  isSelected = false,
  isInPipeline = false,
  onSelect,
  showCheckbox = false
}) => {
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);

  // Calculate opportunity score based on website presence
  // No website = high opportunity (95-100), Has website = lower opportunity (40-60)
  const calculateOpportunityScore = () => {
    if (!business.has_website) return 95;
    if (business.rating && business.rating < 3.5) return 80;
    if (!business.website) return 75;
    return 50;
  };

  const score = calculateOpportunityScore();

  const handleSaveLead = async () => {
    if (onSaveLead) {
      onSaveLead(business.id);
      return;
    }

    setSaving(true);
    try {
      await api.post('/leads', {
        business_profile_id: business.id,
        status: 'New',
      });
      alert('Lead saved successfully!');
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Error saving lead');
    } finally {
      setSaving(false);
    }
  };

  // Generate service gaps and fixes based on business data
  const serviceGaps = [];
  const itFixes = [];

  if (!business.has_website) {
    serviceGaps.push('No website detected - missing online presence');
    itFixes.push('Create professional website with online booking');
  } else if (business.website) {
    if (business.rating && business.rating < 3.5) {
      serviceGaps.push('Low rating suggests customer experience issues');
      itFixes.push('Implement review management system');
    }
    serviceGaps.push('Website may need SEO optimization');
    itFixes.push('Optimize website for local search');
  }

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (onSelect) {
      onSelect(business.id, e.target.checked);
    }
  };

  return (
    <div className={`bg-white rounded-2xl shadow-sm border overflow-hidden hover:shadow-lg transition-all duration-300 ${
      isSaved || isInPipeline ? 'border-amber-400 ring-1 ring-amber-100' : 'border-slate-200'
    } ${isSelected ? 'ring-2 ring-indigo-500' : ''}`}>
      <div className="p-6">
        <div className="flex justify-between items-start gap-4 mb-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-3 mb-1">
              {showCheckbox && (
                <label className="custom-checkbox-large">
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={handleCheckboxChange}
                    disabled={isInPipeline}
                  />
                  <span className="checkbox-label"></span>
                </label>
              )}
              <h3 className="text-xl font-black text-slate-900 truncate">{business.name}</h3>
              <PotentialBadge score={score} />
              {isInPipeline && (
                <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-[10px] font-black uppercase tracking-tighter rounded-full">
                  In Pipeline
                </span>
              )}
            </div>
            <div className="flex flex-col gap-1">
              {business.address && (
                <div className="flex items-center gap-1.5 text-xs text-slate-500 font-medium">
                  <svg className="w-3.5 h-3.5 text-slate-400 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  <span className="truncate">{business.address}</span>
                </div>
              )}
              {business.website ? (
                <div className="flex items-center gap-1.5 text-xs">
                  <svg className="w-3.5 h-3.5 text-indigo-500 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9h18" />
                  </svg>
                  <a 
                    href={business.website.startsWith('http') ? business.website : `https://${business.website}`} 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="text-indigo-600 font-bold hover:underline truncate"
                  >
                    {business.website.replace(/^https?:\/\//, '')}
                  </a>
                </div>
              ) : (
                <div className="flex items-center gap-1.5 text-[10px] font-black text-rose-500 uppercase tracking-tighter">
                  <svg className="w-3.5 h-3.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                  No Website Detected
                </div>
              )}
            </div>
          </div>
          <ScoreBadge score={score} />
        </div>

        {business.rating && (
          <div className="flex items-center gap-2 mb-4 text-sm">
            <span className="text-yellow-500">⭐</span>
            <span className="font-bold text-slate-900">{business.rating}</span>
            {business.review_count && (
              <span className="text-slate-500 text-xs">({business.review_count} reviews)</span>
            )}
          </div>
        )}

        <div className="grid grid-cols-2 gap-4 mb-6">
          <ServiceGapsCard gaps={serviceGaps} />
          <ITFixesCard fixes={itFixes} />
        </div>

        <div className="flex flex-wrap gap-2">
          {!isInPipeline && (
            <button 
              onClick={handleSaveLead}
              disabled={saving}
              className={`px-4 py-2 rounded-lg text-[10px] font-black uppercase tracking-widest transition-all ${
                isSaved 
                  ? 'bg-amber-400 text-white' 
                  : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
              }`}
            >
              {saving ? 'Saving...' : isSaved ? 'In Pipeline' : 'Move to CRM'}
            </button>
          )}
          <button 
            onClick={() => navigate(`/business/${business.id}`)}
            className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-[10px] font-black uppercase tracking-widest hover:bg-indigo-700 transition-all"
          >
            View Full Analysis
          </button>
        </div>
      </div>
    </div>
  );
};

export default BusinessCard;
